import {useCallback} from 'react';
import {useParams} from 'react-router-dom';
import {useRewardsBase} from './useRewardsBase';
import {useLoadDraftFromRoute} from './useLoadDraftFromRoute';
import {
  createHandlePublish,
  createHandleNext,
  createHandlePrevious,
  createHandleDraft
} from './rewardHandlers';

/**
 * Hook for Edit Reward flow
 * Handles edit-specific logic: loads draft on mount, starts at step 4, skips name validation, etc.
 */
export const useRewardsEdit = () => {
  const common = useRewardsBase();
  const {id} = useParams();
  const {
    currentStep,
    formData,
    validation,
    isLoading,
    validateCurrentStep,
    loadDraft,
    buildPayload,
    getDisableDraftFlag,
    navigate,
    dispatch,
    ...restCommon
  } = common;

  useLoadDraftFromRoute({
    id,
    rewardId: formData?.rewardId,
    loadDraft,
    dispatch
  });

  // Handle publishing (defined first so it can be used in handleNext)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePublish = useCallback(
    createHandlePublish({buildPayload, dispatch, navigate}),
    [buildPayload, navigate, dispatch]
  );

  // Handle next step (no name validation for edits)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleNext = useCallback(
    createHandleNext({
      currentStep,
      formData,
      validateCurrentStep,
      dispatch,
      handlePublish,
      shouldValidateName: false
    }),
    [currentStep, formData, validateCurrentStep, dispatch, handlePublish]
  );

  // Handle previous step
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePrevious = useCallback(
    createHandlePrevious({currentStep, dispatch}),
    [currentStep, dispatch]
  );

  // Handle draft saving (no name validation for edits)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDraft = useCallback(
    createHandleDraft({
      currentStep,
      formData,
      buildPayload,
      loadDraft,
      dispatch,
      navigate,
      shouldValidateName: false
    }),
    [formData, buildPayload, loadDraft, navigate, dispatch, currentStep]
  );

  return {
    ...restCommon,
    currentStep,
    formData,
    validation,
    isLoading,
    handleNext,
    handlePrevious,
    handleDraft,
    handlePublish,
    disableDraft: getDisableDraftFlag()
  };
};
